import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'
import { useCallback } from 'react'

import { queryKeys } from '@/api/queryKeys'
import { describeError } from '@/api/errors'
import { useToast } from '@/shared/ui/toast/useToast'

/**
 * Invalida ciò che quasi ogni operazione tocca di riflesso: il saldo crediti e
 * il registro attività. Sono conseguenze di sistema, non della singola vista,
 * e dimenticarle è il modo più facile per mostrare un saldo vecchio.
 */
export function useInvalidateSideEffects() {
  const queryClient = useQueryClient()

  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.credits })
    void queryClient.invalidateQueries({ queryKey: queryKeys.activity })
  }, [queryClient])
}

export interface ApiMutationOptions<TData, TVariables> extends Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
> {
  mutationFn: (variables: TVariables) => Promise<TData>
  /** Messaggio mostrato a operazione riuscita. Omesso quando la vista parla da sé. */
  successMessage?: string | ((data: TData, variables: TVariables) => string)
}

/**
 * Mutazione con la gestione dell'errore già cablata.
 *
 * Ogni fallimento diventa un messaggio che dice cosa è successo e come uscirne
 * — crediti finiti, modulo spento, server muto sono tre rimedi diversi — invece
 * di un "errore" generico o, peggio, di un silenzio.
 */
export function useApiMutation<TData, TVariables>({
  mutationFn,
  successMessage,
  onError,
  onSuccess,
  ...options
}: ApiMutationOptions<TData, TVariables>) {
  const { notify } = useToast()
  const invalidateSideEffects = useInvalidateSideEffects()

  return useMutation<TData, Error, TVariables>({
    mutationFn,
    // Gli argomenti dei callback vengono inoltrati così come arrivano: la loro
    // firma cambia fra versioni di TanStack Query, e riscriverla a mano qui
    // significherebbe romperla al primo aggiornamento.
    onSuccess: (...args) => {
      const [data, variables] = args
      invalidateSideEffects()
      if (successMessage) {
        notify(
          typeof successMessage === 'function' ? successMessage(data, variables) : successMessage,
          'positive',
        )
      }
      onSuccess?.(...args)
    },
    onError: (...args) => {
      notify(describeError(args[0]), 'critical')
      onError?.(...args)
    },
    ...options,
  })
}
